import axios from 'axios';
import { getBaseURL } from '@scoutus/sports-metrics';

/**
 * Client for the bookmark analytics admin API.
 *
 * Goes straight to axios rather than through `@scoutus/sports-metrics`
 * because the generated client is rebuilt from the API's OpenAPI spec by a
 * separate publish and does not include this route yet. It shares the
 * package's base URL and receives the same Auth0 access token from
 * AuthProvider, so behaviour matches the generated calls. Swap for
 * `api.admin.getBookmarkOverview` once the package is regenerated.
 */
const client = axios.create();

let authToken: string | null = null;

/** Called by AuthProvider whenever it refreshes the Auth0 access token. */
export function setBookmarkAnalyticsAuthToken(token: string | null): void {
  authToken = token;
}

client.interceptors.request.use((config) => {
  config.baseURL = getBaseURL();
  if (authToken) {
    config.headers = config.headers ?? {};
    config.headers.Authorization = `Bearer ${authToken}`;
  }
  return config;
});

export interface BookmarkTimePointResponse {
  date: string;
  value: number;
}

export interface BookmarkTopPostResponse {
  activityId: string;
  bookmarkCount: number;
  authorId: number | null;
  text: string | null;
  authorName: string | null;
  createdAt: string | null;
  imageCount: number;
  videoCount: number;
  authorType: string | null;
  authorAdminId: number | null;
}

export interface BookmarkTopAuthorResponse {
  userId: number;
  firstName: string | null;
  lastName: string | null;
  slug: string | null;
  profileImage: string | null;
  userType: string | null;
  adminId: number;
  bookmarkCount: number;
  postCount: number;
}

export interface BookmarkOverviewResponse {
  totalBookmarks: BookmarkTimePointResponse[];
  usersBookmarking: BookmarkTimePointResponse[];
  authorsBookmarked: BookmarkTimePointResponse[];
  topPosts: BookmarkTopPostResponse[];
  topAuthors: BookmarkTopAuthorResponse[];
}

export async function getBookmarkOverview(params: {
  start?: string;
  end?: string;
  limit?: number;
  granularity?: 'day' | 'hour';
}): Promise<BookmarkOverviewResponse> {
  const response = await client.get<{ data: BookmarkOverviewResponse }>(
    '/api/v1/admin/analytics/bookmarks/overview',
    { params },
  );
  return response.data.data;
}
